import { ReactNode, useEffect, useRef } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { Delete, Information } from '@mint-lib/icons';
import { enqueueSnackbar } from '@mint-lib/notifications';
import { useIsAllowedPermission } from '@mint-lib/profile-helpers';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DialogContent,
  DialogFooter,
} from '@mint-lib/tw-components';

import { useFlights } from '../../../../hooks/useFlights.js';
import type { Activity } from '../../../../types/activity.types.js';
import FlightsTable from '../Header/ActionsBar/FlightsDrawer/FlightsTable.jsx';
import NewFlight from '../Header/ActionsBar/FlightsDrawer/NewFlight.jsx';
import NoFlights from '../Header/ActionsBar/FlightsDrawer/NoFlights.jsx';
import { ActivityPreviewCard } from './ActivityPreviewCard.jsx';

type CreateFlightProps = {
  activity: Activity;
  stepper: ReactNode;
  onConfirm: () => void;
};

const NO_LOCKED_FLIGHT_IDS = new Set<string>();

export const CreateFlight = ({
  activity,
  stepper,
  onConfirm,
}: CreateFlightProps) => {
  const { t } = useTranslation('@myn/planning_v2');
  const isReadOnly = !useIsAllowedPermission('campaignPlanningFeature:modify');

  const flightsGridRef = useRef(null);
  const hasAutoCreatedFirstFlight = useRef(false);

  const {
    data: flightsData,
    isLoading: isFlightsLoading,
    handleCreateFlights,
    handleCellUpdate,
    handleDateCellUpdate,
    handleDeleteLastFlights,
  } = useFlights({
    flightsGridRef,
    activityId: activity.id,
    onFlightCreated: () => {
      enqueueSnackbar({
        title: t('Flight successfully created'),
        variant: 'success',
        removable: true,
      });
    },
  });

  useEffect(() => {
    if (!activity.id || isFlightsLoading || hasAutoCreatedFirstFlight.current) {
      return;
    }

    if (flightsData?.items.length === 0) {
      handleCreateFlights();
    }

    hasAutoCreatedFirstFlight.current = true;
  }, [
    activity.id,
    flightsData?.items.length,
    handleCreateFlights,
    isFlightsLoading,
  ]);

  return (
    <>
      <DialogContent className="flex flex-col gap-6">
        {stepper}
        <div className="flex flex-col gap-2">
          <Alert variant="multiline" severity="info">
            <Information />
            <div className="flex flex-col gap-1">
              <AlertTitle>
                {t(
                  'Flights are the time frame planning and governance portion of the activity.',
                )}
              </AlertTitle>
              <AlertDescription>
                {t(
                  'Split activity in as many flights as you need within the activity date.',
                )}
              </AlertDescription>
            </div>
          </Alert>
          <ActivityPreviewCard activity={activity} />
          <div className="flex w-full justify-between p-2">
            <NewFlight
              additional={flightsData?.additional}
              handleCreateFlights={handleCreateFlights}
            />
            <Button
              variant="outlined"
              color="error"
              onClick={handleDeleteLastFlights}
              aria-label="Delete last flight"
              disabled={
                isReadOnly || !flightsData?.additional?.isLastFlightDeletable
              }
              size="medium"
            >
              <Delete />
              {t('Delete last flight')}
            </Button>
          </div>
          <div className="h-90">
            {flightsData?.items.length || isFlightsLoading ? (
              <FlightsTable
                flightsGridRef={flightsGridRef}
                flightsData={flightsData}
                handleDateCellUpdate={handleDateCellUpdate}
                handleCellUpdate={handleCellUpdate}
                isLoading={isFlightsLoading}
                lockedFlightIds={NO_LOCKED_FLIGHT_IDS}
              />
            ) : (
              <NoFlights />
            )}
          </div>
        </div>
      </DialogContent>
      <DialogFooter>
        <Button
          size="large"
          color="primary"
          onClick={onConfirm}
          aria-label="Confirm create flights step"
        >
          {t('Confirm')}
        </Button>
      </DialogFooter>
    </>
  );
};
